import { find, isEmpty, join, map, reduce, trim } from 'lodash'
import moment from 'moment'

import { featureTypes, featureConfigs } from '../../configuration/features'

// UTILITY FUNCTIONS
function getFeatureDetails(featureDetails, type) {
  const feature = find(
    featureDetails,
    (featureDetail) => featureDetail.type === type
  )
  return feature?.details
}

function getFormatedLocation(accommodation) {
  const {
    LocArptInd: locAirport,
    LocCityInd: locCity,
    LocTouristInd: locTouristArea,
  } = accommodation

  let formattedLocation = ''

  if (locAirport) {
    formattedLocation = `${locAirport} Airport`
  }
  if (locCity) {
    if (formattedLocation)
      formattedLocation = join([formattedLocation, `${locCity} City`], ', ')
    else formattedLocation = `${locCity} City`
  }
  if (locTouristArea) {
    if (formattedLocation)
      formattedLocation = join(
        [formattedLocation, `${locTouristArea} Tourist Area`],
        ', '
      )
    else formattedLocation = `${locTouristArea} Tourist Area`
  }

  return formattedLocation
}

function getFormattedPointOfInterest(accommodation) {
  const {
    POIName1: name,
    // POIDesc1: description, // TODO: check if we want to display this
    POIDir1: direction,
    POIDist1: distance,
  } = accommodation

  let formattedPointOfInterest = ''

  if (name) {
    if (direction && distance) {
      formattedPointOfInterest = `Point of Interest: ${name}, ${distance} ${direction}`
    } else if (distance) {
      formattedPointOfInterest = `${name}, ${distance}`
    } else {
      formattedPointOfInterest = name
    }
  }
  return formattedPointOfInterest
}

function getStandardTime(strTime) {
  if (!strTime) return null

  // Example of strTime: '500' = 5:00 AM, '1500' = 3:00 PM
  const hour = strTime.slice(0, -2)
  const minute = strTime.slice(-2)
  const militaryTime = `${hour}:${minute}`

  const standardTime = moment(militaryTime, 'HH:mm').format('h:mm A')
  if (standardTime === 'Invalid date') return ''

  return standardTime
}

function getFormatedCheckInOutTime(checkInTime, checkOutTime) {
  const stdCheckInTime = getStandardTime(checkInTime)
  const stdCheckOutTime = getStandardTime(checkOutTime)

  if (stdCheckInTime && stdCheckOutTime) {
    return `Check In Time: ${stdCheckInTime}, Check Out Time: ${stdCheckOutTime}`
  } else if (stdCheckInTime) {
    return `Check In Time: ${stdCheckInTime}`
  } else if (stdCheckOutTime) {
    return `Check Out Time: ${stdCheckOutTime}`
  } else return ''
}

// FEATURE#1: ADDRESS
function formatAddress(accommodation) {
  const {
    Address: streetAddress,
    Country: country,
    LocDescription: locDescription,
  } = accommodation
  const formattedAddress = []

  // Address
  if (streetAddress && country)
    formattedAddress.push(`${streetAddress}, ${country}`)
  else if (streetAddress) formattedAddress.push(streetAddress)
  else if (country) formattedAddress.push(country)

  // Location: In, At, Near by
  const formattedLocation = getFormatedLocation(accommodation)
  if (formattedLocation) formattedAddress.push(formattedLocation)
  if (locDescription) formattedAddress.push(locDescription)

  // Location: Point of Interest
  const formattedPointOfInterest = getFormattedPointOfInterest(accommodation)
  if (formattedPointOfInterest) formattedAddress.push(formattedPointOfInterest)

  return formattedAddress
}

// FEATURE#2: GENERAL
function formatGeneral(accommodation) {
  const {
    CheckInTime: checkInTime,
    CheckOutTime: checkOutTime,
    AmenRmBroadbandInternetInd: highSpeedInternet,
    AmenRmBroadbandInternetFeeInd: highSpeedInternetFee,
    AmenRmWirelessInternetInd: wirelessInternet,
    AmenRmWirelessInternetFeeInd: WirelessInternetFee,
  } = accommodation

  const formattedGeneral = []
  const formatteddCheckInOutTime = getFormatedCheckInOutTime(
    checkInTime,
    checkOutTime
  )

  if (formatteddCheckInOutTime) formattedGeneral.push(formatteddCheckInOutTime)

  if (highSpeedInternet === 'Available')
    formattedGeneral.push('In Room High-speed Internet Access')
  if (highSpeedInternetFee === 'Available')
    formattedGeneral.push('In Room High-speed Internet Access Fee')
  if (wirelessInternet === 'Available')
    formattedGeneral.push('In Room Wireless Internet Access')
  if (WirelessInternetFee === 'Available')
    formattedGeneral.push('In Room Wireless Internet Access Fee')

  return formattedGeneral
}

// FEATURE#3: AMENITIES
function formatAmenities(accommodation) {
  const {
    AmenRmACInd: airCondition,
    AmenRmBalconyInd: balcony,
    AmenRmCoffeeMakerInd: coffeeMaker,
    AmenRmFireplaceInd: fireplace,
    AmenRmKitchenInd: kitchen,
    AmenRmRefrigeratorInd: refrigerator,
    AmenRecPoolIndoorInd: indoorPool,
    AmenRecPoolOutdoorInd: outdoorPool,
  } = accommodation

  const formattedAmenities = []
  const availabilityCodes = ['All', 'Some', 'A', 'S']
  const nearByCodes = ['Near by', 'N']
  const onsiteCodes = ['On Site', 'O']

  if (availabilityCodes.includes(airCondition))
    formattedAmenities.push('Air Conditioning (In Room)')
  if (availabilityCodes.includes(balcony))
    formattedAmenities.push('Balcony/Terrace')
  if (availabilityCodes.includes(coffeeMaker))
    formattedAmenities.push('Coffee Maker')
  if (availabilityCodes.includes(fireplace))
    formattedAmenities.push('Fireplace')
  if (availabilityCodes.includes(kitchen))
    formattedAmenities.push('Kitchen/Kitchenette')
  if (availabilityCodes.includes(refrigerator))
    formattedAmenities.push('Refrigerator')

  if (nearByCodes.includes(indoorPool))
    formattedAmenities.push('Indoor Pool Near by')
  if (onsiteCodes.includes(indoorPool))
    formattedAmenities.push('Indoor Pool Onsite')
  if (nearByCodes.includes(outdoorPool))
    formattedAmenities.push('Outdoor Pool Near by')
  if (onsiteCodes.includes(outdoorPool))
    formattedAmenities.push('Outdoor Pool Onsite')

  return formattedAmenities
}

// FEATURE#4: DINING
function formatDining(accommodation) {
  const {
    MealIncAPInd: americanPlan,
    MealIncCPInd: continentalPlan,
    MealIncFBInd: fullBreakfastPlan,
    MealIncMAPInd: modifiedAmerican,
    RestrName1: restaurantName,
    RestrType1: restaurantTypeCode,
    RestrOnSiteInd1: restaurantOnsite,
    RestrDesc1: restaurantDesc,
  } = accommodation
  const formattedDining = []

  function getRestaurantType(typeCode) {
    // TODO: check typecode / test more when have more real data
    switch (typeCode) {
      case 'B' || 'Bar':
        return 'Bar'
      case 'C' || 'Coffee Shop':
        return 'Coffee Shop'
      case 'R' || 'Restaurant':
        return 'Restaurant'
      default:
        return ''
    }
  }

  if (americanPlan === 'Available') formattedDining.push('American Plan')
  if (continentalPlan === 'Available') formattedDining.push('Continental Plan')
  if (fullBreakfastPlan === 'Available') formattedDining.push('Full Breakfast')
  if (modifiedAmerican === 'Available')
    formattedDining.push('Modified American')

  const restaurantType = getRestaurantType(restaurantTypeCode)
  if (restaurantName && restaurantType) {
    formattedDining.push(`${restaurantName}, ${restaurantType}`)
  } else if (restaurantName) {
    formattedDining.push(restaurantName)
  } else if (restaurantType) {
    formattedDining.push(restaurantType)
  }

  // TODO: check if it's 'Y' or 'Yes'
  if (restaurantOnsite === 'Yes') formattedDining.push('Restaurant Onsite')
  if (restaurantDesc) formattedDining.push(restaurantDesc)

  return formattedDining
}

// FEATURE#5: ADDITIONAL AMENTITIES
function formatValueAdd(accommodation) {
  const {
    AmenHtlCompCoffeeInd: complementaryCoffee,
    AmenHtlCompTransInd: complementaryTransportation,
    ArchitectureAmbience: architectureAmbience,
  } = accommodation
  const formattedValueAdd = []

  if (complementaryCoffee === 'Available') {
    formattedValueAdd.push('Complementary Coffee')
  }
  if (complementaryTransportation === 'Available') {
    formattedValueAdd.push('Complementary Transportation')
  }
  // TODO: confirm if the `ArchitectureAmbience` field should be included in this category
  if (architectureAmbience) formattedValueAdd.push(architectureAmbience)

  return formattedValueAdd
}

// FEATURE#6: PARKING
function formatParking(accommodation) {
  const {
    AmenPrkgOnFreeInd: parkingOnsiteFree,
    AmenPrkgOnPaidInd: parkingOnsitePaid,
  } = accommodation

  const formattedParking = []
  if (parkingOnsiteFree === 'Available') {
    formattedParking.push('Parking On Site Free')
  }
  if (parkingOnsitePaid === 'Available') {
    formattedParking.push('Parking On Site Paid')
  }

  return formattedParking
}

// FEATURE#7: AIRPORT
function formatAirport(accommodation) {
  const {
    ArptCode1: airportCode,
    ArptName1: airportName,
    ArptDir1: airportDirection,
    ArptDist1: airportDistance, // e.g. '35 mi'
    // TODO: confirm if `airportDistanceUnit` will not be used
    // ArptDistUnit1: airportDistanceUnit, // not included in the dummy data
    ArptDriveTime1: airportDrivingTime,
    ArptFreeTransInd1: airportFee,
    // TODO: airport#2
    // ArptCode2: airportCode2,
    // ArptName2: airportName2,
    // ArptDir2: airportDirection2,
    // ArptDist2: airportDistance2,
    // ArptDistUnit2: airportDistanceUnit2, // not included in the dummy data
    // ArptTime2: airportDrivingTime2,
    // ArptFreeTransInd2: airportFee2,
  } = accommodation
  const formattedAirport = []

  if (airportName && airportCode)
    formattedAirport.push(`${airportName}, ${airportCode}`)
  if (airportDistance) {
    if (airportDirection)
      formattedAirport.push(
        `Airport Distance: ${airportDistance}, ${airportDirection}`
      )
    else formattedAirport.push(`Airport Distance: ${airportDistance}`)
  }
  if (airportDrivingTime)
    formattedAirport.push(`Airport Driving Time: ${airportDrivingTime}`)
  if (airportFee === 'Available')
    formattedAirport.push('Airport Free Transportation')
  return formattedAirport
}

export function getFormattedLeonardoFeatures(accommodation, amenitiesFallback) {
  if (!accommodation) return null

  const accommodationWithTrimmedFields = reduce(accommodation, (formattedFields, field, key) => ({
    ...formattedFields,
    [`${key}`]: trim(field)
  }))

  const amenities = formatAmenities(accommodationWithTrimmedFields)

  const featureDetails = [
    { type: featureTypes.address, details: formatAddress(accommodationWithTrimmedFields) },
    {
      type: featureTypes.general,
      details: formatGeneral(accommodationWithTrimmedFields),
    },
    { type: featureTypes.amenities, details: isEmpty(amenities) ? amenitiesFallback : amenities },
    { type: featureTypes.dining, details: formatDining(accommodationWithTrimmedFields) },
    {
      type: featureTypes.additionalAmentities,
      details: formatValueAdd(accommodationWithTrimmedFields),
    },
    { type: featureTypes.parking, details: formatParking(accommodationWithTrimmedFields) },
    { type: featureTypes.airport, details: formatAirport(accommodationWithTrimmedFields) },
  ]

  const formattedFeatures = map(featureConfigs, (feature) => ({
    ...feature,
    details: getFeatureDetails(featureDetails, feature?.type),
  }))

  return formattedFeatures
}
